import CASBlock from "@blocks/CASBlock/CASBlock";
import PromoteEpaper from "@blocks/PromoteEpaper/PromoteEpaper";
import BudgetHomeWidget from "@components/Layouts/Home/BudgetHomeWidget";
import { CinemaArticles } from "@components/Layouts/Home/CinemaArticles";
import { HoroscopeSection } from "@components/Layouts/Home/HoroscopeSection";
import { JobsListing } from "@components/Layouts/Home/JobsListing";
import { MovieTimingsSection } from "@components/Layouts/Home/MovieTimingsSection";
import { RecentArticlesSection } from "@components/Layouts/Home/RecentArticlesSection";
import { SectionArticles } from "@components/Layouts/Home/SectionArticles";
import { TopNewsSection } from "@components/Layouts/Home/TopNewsSection";
import { VideoPlaylistSection } from "@components/Layouts/Home/VideoPlaylistSection";
import MainBlock from "@container/MainBlock";
import OutbrainMainContainer, {
  AdsSettings,
} from "@container/OutbrainMainContainer";
import { GoldSection } from "@container/SideBlock/GoldSection";
import SideBlock from "@container/SideBlock/SideBlock";
import WrapperBlock from "@container/WrapperBlock";
import { AdsImu2 } from "@elements/Advertisement/variant/AdsImu";
import Divider from "@elements/Divider/Divider";
import {
  BC_PLAYER_ID_HOME,
  HOME_CAS_CAMPAIGN_ID,
} from "@helper/getEnvVariables";
import useCasScript from "@hooks/useCasScript";
import useGlobalAdSetting from "@hooks/useGlobalAdSetting";
import useWindowDimensions from "@hooks/useWindowDimensions";
import HomeMetaData from "@pages/Home/HomeMetaData";
import type { TRouteWithoutRedirect } from "@sphtech/web2-core/ssr";
import { CustomContext, useRouteContext } from "@sphtech/web2-core/ssr";
import { ResponseType } from "@sphtech/web2-core/ssr";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import { HomePageDataPayload } from "@typings/PagePayloadType";

export default function HomePage() {
  const routeContext: CustomContext<
    TRouteWithoutRedirect<HomePageDataPayload, string>
  > = useRouteContext();
  const dataLoaderResponse = routeContext.context;
  const adSettings: AdsSettings = useGlobalAdSetting();
  if (dataLoaderResponse.type === ResponseType.CLIENT_ERROR) {
    throw new Error("Client Error");
  } else if (dataLoaderResponse.type === ResponseType.SERVER_ERROR) {
    throw new Error("Server Error");
  }

  const { isScriptReady: isCasScriptReady } = useCasScript();

  const { gaData, metaTags } = HomeMetaData();
  const pageAdTargetValue = PageAdTargetingTypeEnum.HOME;

  const sectionName = "/";
  const { width } = useWindowDimensions();
  const isMobile = width <= 480;

  const recentArticles = dataLoaderResponse.payload.recentArticles ?? [];
  const topNewsArticles = dataLoaderResponse.payload.topNewsArticles ?? [];
  const epaperCoverImageUrl =
    dataLoaderResponse.payload.epaperCoverImage?.coverImageUri || "";
  const sectionArticles = dataLoaderResponse.payload.sectionArticles ?? {};

  const bcVideos = dataLoaderResponse.payload.bcVideos;

  const goldPrices = dataLoaderResponse.payload.goldPrices;
  const forexPairs = dataLoaderResponse.payload.forexPairs;
  const movies = dataLoaderResponse.payload.movies;
  const jobs = dataLoaderResponse.payload.jobs;

  const mostRead = dataLoaderResponse.payload.mostRead;
  const sidebarSections = dataLoaderResponse.payload.sidebarSections;

  const budgetPayload = dataLoaderResponse.payload.budgetPayload;

  return (
    <OutbrainMainContainer
      gaData={gaData}
      metaTagsProps={metaTags}
      sectionName={sectionName}
      pageAdTargetValue={pageAdTargetValue}
      suppliedAdsSettings={adSettings}
      eventBanner={budgetPayload ? false : true}
    >
      {budgetPayload && <BudgetHomeWidget {...budgetPayload} />}
      <WrapperBlock className="flex flex-col lg:flex-row justify-between">
        <MainBlock showBreadcrumbs={false} showTitle={false}>
          <>
            <TopNewsSection topNewsArticles={topNewsArticles} />
            {isMobile && (
              <>
                <AdsImu2
                  sectionName="home"
                  pageAdTargetType={pageAdTargetValue}
                />
                <Divider className="border-b border-gray-2200" />
                <PromoteEpaper
                  className="lg:hidden border-t-0 pt-0"
                  epaperCoverImageUrl={epaperCoverImageUrl}
                />
              </>
            )}

            <RecentArticlesSection recentArticles={recentArticles} />
            {isCasScriptReady && HOME_CAS_CAMPAIGN_ID && (
              <CASBlock casId={HOME_CAS_CAMPAIGN_ID} />
            )}
            <VideoPlaylistSection
              playerId={BC_PLAYER_ID_HOME}
              bcVideos={bcVideos}
            />
            <SectionArticles {...sectionArticles} />
            {isMobile && goldPrices?.status == 200 && (
              <>
                {" "}
                <GoldSection goldPrices={goldPrices} />
                <Divider className="mt-5 border-b border-gray-2200" />
              </>
            )}
            {jobs?.status == 200 && <JobsListing jobs={jobs} />}
            <HoroscopeSection />
            {movies?.status == 200 && <MovieTimingsSection movies={movies} />}
            <CinemaArticles articlesPayload={sectionArticles.cinemaArticles} />
          </>
        </MainBlock>

        <SideBlock
          sectionName="home"
          hasTaboola={adSettings.isTaboolaEnabled}
          pageAdTargetValue={pageAdTargetValue}
          hasTranslate
          promoteEpaper={!isMobile}
          epaperCoverImageUrl={epaperCoverImageUrl}
          mostRead={mostRead}
          sidebarSections={sidebarSections}
          promoteMITTBanner={true}
          {...(forexPairs?.response == 200 && { forexPairs })}
          {...(!isMobile && goldPrices?.status == 200 && { goldPrices })}
        />
      </WrapperBlock>
    </OutbrainMainContainer>
  );
}
